import React, { useState, useRef, useEffect } from 'react';
import { styled } from 'styled-components';
import { FlexBoxHorizontal, FlexBoxVertical, FlexBox, Label, TranslateTypography, TextField, Textarea } from '../../atoms';
import { SelectAllCheckBox } from '../../molecules';
import { Add, Delete, Edit } from '@mui/icons-material';
import palette from '../../../utils/colorPalette';
import i18n from '../../../utils/i18n'
import { translateText } from '../../../utils/translateText';
import { useTranslation } from 'react-i18next';
import { DataGridPremium } from '@mui/x-data-grid-premium';

export const ChangeCommentTab = ({ data }) => {
      const { t } = useTranslation();
      const language = i18n.language;
      const [commentData, setCommentData] = useState([]);
      const [userData, setUserData] = useState([]);
      const [mode, setMode] = useState('default');
      const [selectedUser, setSelectedUser] = useState(null);
      const [selectedDt, setSelectedDt] = useState(null);
      const [selectedImages, setSelectedImages] = useState([]);
      const [uploadProgress, setUploadProgress] = useState(null);
      const [selectedComment, setSelectedComment] = useState(null);
      const [changeComment, setChangeComment] = useState(null);
      const [isSaving, setIsSaving] = useState(false);
      const [isLoadingImages, setIsLoadingImages] = useState(false);
      const dropZoneRef = useRef(null); 
      const [translatedColumns, setTranslatedColumns] = useState(columns(language));

      console.log(commentData);
      useEffect(() => {
        if (!data) return;
        setSelectedComment(null);
        setMode('default');
        setSelectedUser('');
        setSelectedDt('');
        setChangeComment('');
        setSelectedUser('');
        setSelectedDt('');
        fetchCommentData();
      }, [data]); // data 변경 감지

      // 붙여넣기 이벤트 리스너 추가
      useEffect(() => {
        const handlePaste = (e) => {
          // 편집 모드가 아니면 복사 붙여넣기 비활성화
          if (mode === 'default') return;
          
          // 다른 입력 필드에 포커스가 있으면 복사 붙여넣기 비활성화
          const activeElement = document.activeElement;
          if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
            return;
          }

          const clipboardData = e.clipboardData || window.clipboardData;
          const items = clipboardData.items;

          const imageFiles = [];
          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.indexOf('image') !== -1) {
              const blob = item.getAsFile();
              if (blob) {
                // Blob을 File로 변환 (파일명 추가)
                const file = new File([blob], `image_${Date.now()}.png`, { type: blob.type });
                imageFiles.push(file);
              }
            }
          }

          if (imageFiles.length > 0) {
            e.preventDefault();
            // 가상 이벤트 객체 생성
            const fakeEvent = {
              target: { files: imageFiles, value: '' }
            };
            handleImageSelectForComment(fakeEvent);
          }
        };

        // 이벤트 리스너 등록
        document.addEventListener('paste', handlePaste);

        // 클린업 함수
        return () => {
          document.removeEventListener('paste', handlePaste);
        };
      }, [mode]); // mode가 변경될 때마다 리스너 재등록

      const fetchCommentData = async (autoSelectFirst = true) => {
        try {
          const response = await fetch('/api/cad-data/get-change-comment', {
            method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ brd_cd: data.brd_cd, sesn: data.gtm_sesn, part_cd: data.part_cd })  
          });
          const result = await response.json();
          setCommentData(result.comment || []);
          setUserData(result.user || []);
          
          // autoSelectFirst가 true일 때만 첫 번째 코멘트를 자동 선택
          if (autoSelectFirst && result.comment && result.comment.length > 0) {
            setSelectedComment(result.comment[0]);
          } else if (!autoSelectFirst) {
            setSelectedComment(null);
          }
        } catch (error) {
          console.error('Error fetching comment data:', error);
        }
      };

      const handleComment = async (type) => {
        setMode(type);
        
        if (type === 'edit') {
          if (!selectedComment) {
            alert('수정할 댓글을 선택해주세요.');
            return;
          }
          setChangeComment(selectedComment.comment);
          setSelectedUser({value: selectedComment.change_by, label: selectedComment.change_by});
          setSelectedDt(new Date(selectedComment.change_dt).toISOString().slice(0, 10));
          
          // edit 모드에서는 기존 이미지를 S3에서 가져와서 selectedImages에 추가
          setSelectedImages([]);
          
          if (selectedComment.image_cnt > 0) {
            setIsLoadingImages(true);
            try {
              const response = await fetch('/api/cad-data/get-existing-images', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  brd_cd: 'X',
                  sesn: data.gtm_sesn,
                  part_cd: data.part_cd,
                  seq: selectedComment.seq,
                  image_count: selectedComment.image_cnt
                })
              });
              
              const result = await response.json();
              if (result.success && result.images.length > 0) {
                // 기존 이미지들을 File 객체로 변환하여 selectedImages에 추가
                const existingImageFiles = result.images.map((img, index) => {
                  // base64를 Blob으로 변환
                  const byteCharacters = atob(img.dataUrl.split(',')[1]);
                  const byteNumbers = new Array(byteCharacters.length);
                  for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                  }
                  const byteArray = new Uint8Array(byteNumbers);
                  const blob = new Blob([byteArray], { type: 'image/jpeg' });
                  
                  // File 객체 생성
                  const file = new File([blob], img.fileName, { type: 'image/jpeg' });
                  return {
                    ...file,
                    isExisting: true,
                    dataUrl: img.dataUrl, // dataUrl 유지
                    originalImageNumber: img.imageNumber
                  };
                });
                
                setSelectedImages(existingImageFiles);
              }
            } catch (error) {
              console.error('기존 이미지 로드 실패:', error);
            } finally {
              setIsLoadingImages(false);
            }
          } else {
            setIsLoadingImages(false);
          }
        } else {
          setSelectedUser(null);
          setSelectedDt(new Date().toISOString().slice(0, 10));
          setChangeComment(null);
          setSelectedImages([]);
        }
      };

      const handleCancelEdit = () => {
        setMode('default');
        setChangeComment(null);
        setSelectedUser(null);
        setSelectedDt(null);
        setSelectedImages([]);  // 이미지 목록 리셋 추가
      };

      // 새로 추가된 코멘트의 정보를 가져오는 함수
      const getLatestCommentAfterAdd = async () => {
        try {
          const response = await fetch('/api/cad-data/get-change-comment', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ brd_cd: data.brd_cd, sesn: data.gtm_sesn, part_cd: data.part_cd })  
          });
          const result = await response.json();
          
          if (result.comment && result.comment.length > 0) {
            // update_dt 기준으로 내림차순 정렬하여 가장 최신 코멘트 반환
            const sortedComments = result.comment.sort((a, b) => 
              new Date(b.update_dt) - new Date(a.update_dt)
            );
            return sortedComments[0];
          }
          return null;
        } catch (error) {
          console.error('Error fetching latest comment:', error);
          return null;
        }
      };


      // 이미지 개수 업데이트 함수
      const updateImageCount = async (seq, imageCount) => {
        try {
          const response = await fetch('/api/cad-data/update-image-count', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              brd_cd: data.brd_cd,
              sesn: data.gtm_sesn,
              part_cd: data.part_cd,
              seq: seq,
              image_count: imageCount
            })
          });

          const result = await response.json();
          
          if (!result.success) {
            console.error('image_cnt 업데이트 실패:', result.message);
          }
        } catch (error) {
          console.error('image_cnt 업데이트 중 오류:', error);
        }
      };


      const handleDeleteComment = async (createDt, createBy, comment, seq) => {
        if (!window.confirm(`${createDt}\n${createBy}\n${comment}\n${language === 'ko' ? '삭제하시겠습니까?' : 'Are you sure you want to delete?'}`)) {
          return;
        }
      
        try {
          // 코멘트 삭제 (use_yn을 'N'으로 변경)
          const response = await fetch('/api/cad-data/delete-change-comment', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              brd_cd: data.brd_cd,
              sesn: data.gtm_sesn,
              part_cd: data.part_cd,
              seq: seq
            })
          });
      
          const result = await response.json();
          
          if (result.success) {
            await fetchCommentData(); // 데이터 새로고침
            
            // 삭제된 코멘트이 선택된 코멘트이었다면 선택 해제하고 최신 코멘트 선택
            if (selectedComment && selectedComment.seq === seq) {
              const response = await fetch('/api/cad-data/get-change-comment', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ brd_cd: data.brd_cd, sesn: data.gtm_sesn, part_cd: data.part_cd })  
              });
              const updatedResult = await response.json();
              if (updatedResult.comment && updatedResult.comment.length > 0) {
                setSelectedComment(updatedResult.comment[0]);
              } else {
                setSelectedComment(null);
              }
            }
            
            alert(language === 'ko' ? '코멘트가 삭제되었습니다.' : 'Comment has been deleted.');
          } else {
            alert(language === 'ko' ? '코멘트 삭제에 실패했습니다.' : 'Failed to delete comment.');
          }
        } catch (error) {
          console.error('Error deleting comment:', error);
          alert(language === 'ko' ? '코멘트 삭제 중 오류가 발생했습니다.' : 'An error occurred while deleting the comment.');
        }
      };

    
      // 편집 모드에서 이미지 선택
      const handleImageSelectForComment = (event) => {
        const files = Array.from(event.target.files);
        const maxFileSize = 10 * 1024 * 1024; // 10MB
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        
        const validFiles = [];
        const invalidFiles = [];

        files.forEach(file => {
          if (!allowedTypes.includes(file.type)) {
            invalidFiles.push(`${file.name}: 지원하지 않는 파일 형식`);
          } else if (file.size > maxFileSize) {
            invalidFiles.push(`${file.name}: 파일 크기가 너무 큽니다 (최대 10MB)`);
          } else {
            validFiles.push(file);
          }
        });

        if (invalidFiles.length > 0) {
          alert(`다음 파일들을 업로드할 수 없습니다:\n${invalidFiles.join('\n')}`);
        }

        if (validFiles.length > 0) {
          console.log('validFiles', validFiles);
          setSelectedImages(prev => {
            console.log('prev', prev);
            const totalImages = prev.length + validFiles.length;
            if (totalImages > 10) {
              alert(language === 'ko' ? '최대 10개의 이미지만 선택할 수 있습니다.' : 'You can only select up to 10 images.');
              return [...prev];
            }
            return [...prev, ...validFiles];
          });
        }

        // input 값 초기화 (같은 파일 다시 선택 가능하게)
        event.target.value = '';
      };

      // 선택된 이미지 제거
      const removeSelectedImage = (index) => {
        setSelectedImages(prev => prev.filter((_, i) => i !== index));
      };

      // 드래그 앤 드롭 이벤트 처리
      const handleDragOverForComment = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };

      const handleDropForComment = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const files = Array.from(e.dataTransfer.files);
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        
        if (imageFiles.length > 0) {
          // 가상 이벤트 객체 생성
          const fakeEvent = {
            target: { files: imageFiles, value: '' }
          };
          handleImageSelectForComment(fakeEvent);
        }
      };

      // 모든 이미지 제거
      const clearAllImages = () => {
        setSelectedImages([]);
      };

      // 데이터 그리드에서 행 선택 처리
      const handleRowClick = (params, event) => {
        event.preventDefault();
        event.stopPropagation();
        if (mode !== 'default') {
          setMode('default');
        }
        const selectedRow = commentData.find(comment => comment.seq === params.row.seq);
        if (selectedRow) {
          setSelectedComment(selectedRow);
          setChangeComment(selectedRow.comment);
          setSelectedUser({value: selectedRow.change_by, label: selectedRow.change_by});
          setSelectedDt(selectedRow.change_dt);
          setSelectedImages([]);  // 이미지 목록 리셋 추가
        }
      };
      

      // 최근 변경사항 가져오기
      const getLatestComment = () => {
        if (commentData && commentData.length > 0) {
          return commentData[0]; // 이미 change_dt DESC로 정렬되어 있음
        }
        return null;
      };

      const latestComment = getLatestComment();
    


      // handleSave 함수 추가
      const handleSave = async (type) => {
        if (!changeComment || !selectedUser || !selectedDt) {
          alert(language === 'ko' ? '모든 필드를 입력해주세요.' : 'Please fill in all fields.');
          return;
        }

        setIsSaving(true);
        try {
          // 1. 먼저 코멘트 추가/수정
          const response = await fetch(`/api/cad-data/${type==='add' ? 'add' : 'update'}-change-comment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              brd_cd: data.brd_cd,
              sesn: data.gtm_sesn,
              part_cd: data.part_cd,
              comment: changeComment,
              change_dt: selectedDt,
              change_by: selectedUser.value,
              seq: type === 'add' ? null : selectedComment.seq,
            })
          });

          const result = await response.json();
          
          if (result.success) {
            let targetSeq;
            
            if (type === 'add') {
              // 새로 추가된 코멘트의 seq을 위해 데이터 새로고침
              await fetchCommentData();
              const latestComment = await getLatestCommentAfterAdd();
              targetSeq = latestComment ? latestComment.seq : null;
            } else {
              targetSeq = selectedComment.seq;
            }

// 2. Edit 모드일 때는 기존 이미지들을 모두 삭제하고 새로 업로드
if (type === 'edit' && targetSeq) {
  try {
    console.log('Edit 모드: 기존 이미지 삭제 시작...');
    
    // 기존 이미지들 모두 삭제 (image_count 포함)
    const deleteResponse = await fetch('/api/cad-data/delete-change-comment-images', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        brd_cd: data.brd_cd,
        sesn: data.gtm_sesn,
        part_cd: data.part_cd,
        seq: targetSeq,
        image_count: selectedComment.image_cnt // 기존 이미지 개수 전달
      })
    });
    
    if (deleteResponse.ok) {
      const deleteResult = await deleteResponse.json();
      console.log('기존 이미지 삭제 완료:', deleteResult);
    } else {
      console.error('기존 이미지 삭제 API 응답 에러:', deleteResponse.status);
      // 삭제 실패해도 계속 진행 (이미지가 없을 수도 있음)
    }
  } catch (deleteError) {
    console.error('기존 이미지 삭제 실패:', deleteError);
    // 삭제 실패해도 계속 진행
  }
}
// 3. 새 이미지들 업로드 (Edit 모드에서는 기존 이미지 삭제 후, Add 모드에서는 새로 추가)
if (selectedImages.length > 0) {
  try {
    console.log('새 이미지 업로드 시작...', selectedImages.length);
    
    // 각 이미지를 순차적으로 업로드
    for (let i = 0; i < selectedImages.length; i++) {
      const file = selectedImages[i];
      const formData = new FormData();
      
      // File 객체가 dataUrl 속성을 가지고 있다면 순수한 File 객체로 변환
      let fileToUpload = file;
      if (file.isExisting && file.dataUrl) {
        // 기존 이미지의 경우 dataUrl에서 Blob을 다시 생성
        const byteCharacters = atob(file.dataUrl.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let j = 0; j < byteCharacters.length; j++) {
          byteNumbers[j] = byteCharacters.charCodeAt(j);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/jpeg' });
        fileToUpload = new File([blob], file.name || file.fileName, { type: 'image/jpeg' });
      }
      
      // 파일 추가
      formData.append('file', fileToUpload);
      
      // 메타데이터 추가
      formData.append('part_cd', data.part_cd);
      formData.append('sesn', data.gtm_sesn);
      formData.append('brd_cd', data.brd_cd);
      formData.append('seq', targetSeq);
      formData.append('file_number', i + 1); // 1-based 번호
      formData.append('mode', type); // 'add' 또는 'edit'
      
      const uploadResponse = await fetch('/api/cad-data/change-comment-image-upload', {
        method: 'POST',
        body: formData
      });
      
      if (uploadResponse.ok) {
        console.log(`이미지 ${i + 1} 업로드 완료`);
      } else {
        console.error(`이미지 ${i + 1} 업로드 실패:`, uploadResponse.status);
      }
    }
    
    // 이미지 개수 업데이트
    await updateImageCount(targetSeq, selectedImages.length);
    
    console.log('모든 이미지 업로드 완료');
  } catch (uploadError) {
    console.error('이미지 업로드 실패:', uploadError);
    alert('이미지 업로드 중 오류가 발생했습니다.');
  }
}

            setMode('default');
            setChangeComment(null);
            setSelectedUser(null);
            setSelectedDt(null);
            setSelectedImages([]);
            setUploadProgress(null);
            await fetchCommentData(false); // 자동 선택하지 않음
            
            // 추가/수정된 코멘트 선택
            let targetComment = null;
            
            // 추가/수정 모두 update_dt가 최신인 코멘트를 선택
            targetComment = await getLatestCommentAfterAdd();
            
            alert(language === 'ko' ? 
              `코멘트가 ${type === 'add' ? '추가' : '수정'}되었습니다.` : 
              `Comment has been ${type === 'add' ? 'added' : 'updated'}.`
            );
            
            // 추가/수정된 코멘트 선택
            if (targetComment) {
              setSelectedComment(targetComment);
            }
            
          } else {
            alert(language === 'ko' ? `코멘트 ${type === 'add' ? '추가' : '수정'}에 실패했습니다.` : `Failed to ${type === 'add' ? 'add' : 'update'} comment.`);
          }
        } catch (error) {
          console.error(`Error ${type === 'add' ? 'adding' : 'updating'} comment:`, error);
          alert(`코멘트 ${type === 'add' ? '추가' : '수정'} 중 오류가 발생했습니다.`);
        } finally {
          setIsSaving(false);
        }
      };

      useEffect(() => {
        setTranslatedColumns(columns(language).map((column) => {
          return {
            ...column, 
            headerName: translateText({
              text: column.headerName,
              globalLanguage: language,
            }),
          };
        }));
      }, [language]);
    
      return (
        <FlexBoxVertical 
          style={{justifyContent: 'flex-start', overflowX: 'hidden'}}
        >

          {/* 편집 모드용 다중 이미지 선택 필드 (숨김) */}
          <input
            type="file"
            id="comment-image-input"
            onChange={handleImageSelectForComment}
            accept="image/*"
            multiple
            style={{ display: 'none' }}
          />

          {/* 상단부: 코드 및 브랜드 */}
          <FlexBoxHorizontal style={{ height: '40px', margin: '10px 10px 0 10px' }}>
            <Label style={{ width: '45%', margin: '3px' }}>
              <TranslateTypography variant="content2">
                {data && data['part_cd'] ? data['part_cd'] : '품번'}
              </TranslateTypography>
            </Label>
            <Label style={{ width: '45%', margin: '3px' }}>
            {language === 'ko' ? (
            <TranslateTypography variant="content2">
              {data && data['prdt_nm'] ? data['prdt_nm'] : '제품명 '}
            </TranslateTypography>
          ) : (
            <TranslateTypography variant="content2">
              {data && data['prdt_nm_eng'] ? data['prdt_nm_eng'] : 'Product Name'}
            </TranslateTypography>
          )}
            </Label>
          </FlexBoxHorizontal>

              <FlexBoxVertical style={{ 
                margin: '10px 20px', 
                padding: '20px', 
                border: '1px solid rgb(224, 221, 221)',
                borderRadius: '8px',
                alignItems: 'flex-start',
                width: '-webkit-fill-available',
                position: 'relative',
                height: 'auto',
              }}>
                {
                  commentData.length === 0  && mode === 'default' ? (
                    <>
                      <div style={{ 
                        position: 'absolute',
                        top: '8px',
                        right: '8px'
                      }}>
                        <button
                          disabled={!data}
                          onClick={() => handleComment('add')}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '11px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px',
                            minWidth: '50px',
                            height: '24px',
                          }}
                        >
                          <Add style={{ fontSize: '13px', backgroundColor: palette.button.primary, borderRadius: '10px', color: 'white', padding: '1px' }} />
                          Add
                        </button>
                      </div>
                      <div style={{ height: '130px', textAlign: 'center', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <TranslateTypography style={{ 
                          fontSize: '13px', 
                          color: 'rgb(119, 119, 119)',
                          marginTop: '10px'
                        }}>
                          {data ? '변경 히스토리가 없습니다.' : '스타일을 선택하세요'}
                        </TranslateTypography>
                      </div>
                      </>
                  ) : (
                    <>
                    <FlexBoxHorizontal style={{ justifyContent: 'space-between', marginBottom: '10px' }}>
                  <TranslateTypography style={{ 
                    fontWeight: 'bold',
                    fontSize: '16px',
                    // color: '#2c3e50',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    변경 내용
                  </TranslateTypography>
                      {
                      mode === 'default' && selectedComment && (
                        <div style={{ 
                          display: 'flex',
                          position: 'absolute',
                          top: '7px',
                          right: '7px'
                        }}>
                        <button
                        onClick={() => handleComment('edit')}
                        disabled={!selectedComment}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '11px',
                          fontWeight: '500',
                          cursor: selectedComment ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                          minWidth: '50px',
                          height: '24px'
                        }}
                      >
                        <Edit style={{ fontSize: '13px', backgroundColor: 'rgb(146 146 147)', borderRadius: '10px', color: 'white', padding: '1px' }} />
                        Edit
                      </button>
                      <button
                        onClick={() => selectedComment && handleDeleteComment(selectedComment.change_dt, selectedComment.change_by, selectedComment.comment, selectedComment.seq)}
                        disabled={!selectedComment}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '11px',
                          fontWeight: '500',
                          cursor: selectedComment ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                          minWidth: '50px',
                          height: '24px'
                        }}
                      >
                        <Delete style={{ fontSize: '13px', backgroundColor: 'rgb(146 146 147)', borderRadius: '10px', color: 'white', padding: '1px' }} />
                        Delete
                      </button>
                      <button
                        onClick={() => handleComment('add')}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '11px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                          minWidth: '50px',
                          height: '24px'
                        }}
                      >
                        <Add style={{ fontSize: '13px', backgroundColor: palette.button.primary, borderRadius: '10px', color: 'white', padding: '1px' }} />
                        Add
                      </button>
                    </div>
                      )
                    }
                    </FlexBoxHorizontal>
                    { mode === 'default' ? (
                    <>
                    <FlexBoxHorizontal style={{ justifyContent: 'flex-start' }}>
                      <TranslateTypography style={{ 
                        fontSize: '12px',
                        color: '#555',
                        whiteSpace: 'nowrap',
                        marginRight: '10px',
                      }}>
                        변경일자
                      </TranslateTypography>
                      <TextField 
                        defaultValue={selectedComment ? selectedComment.change_dt.split(" ")[0] : ''} 
                        style={{ marginRight: '10px'}} 
                        readOnly 
                        type='text'
                      ></TextField>
                      <TranslateTypography style={{ 
                        fontSize: '12px',
                        color: '#555',
                        whiteSpace: 'nowrap',
                        margin: '0 10px',
                      }}>
                        작성자
                      </TranslateTypography>
                          <TextField 
                            defaultValue={selectedComment ? selectedComment.change_by : ''} 
                            readOnly  
                          ></TextField>
                    </FlexBoxHorizontal>
                  
                    <FlexBoxHorizontal style={{ width: '100%', justifyContent: 'flex-start', marginBottom: '10px' }}>
                      <TranslateTypography style={{ 
                        fontSize: '12px',
                        color: '#555',
                        whiteSpace: 'nowrap',
                        marginRight: '10px',
                      }}>
                        변경사항
                      </TranslateTypography>
                      <Textarea
                        style={{ height: '50px', width: '90%' }}
                        defaultValue={selectedComment ? selectedComment.comment : ''}
                        readOnly
                        key={selectedComment ? selectedComment.comment : ''}
                      />
                    </FlexBoxHorizontal>

                    {/* default 모드에서 이미지 갤러리 표시 */}
                    {selectedComment && (
                      <FlexBoxHorizontal style={{ width: '100%', justifyContent: 'flex-start' }}>
                        <ImageGallery selectedComment={selectedComment} />
                      </FlexBoxHorizontal>
                    )}
                    </>
                    ) : (
                    <>
                    <FlexBoxHorizontal style={{ justifyContent: 'flex-start' }}>
                    <TranslateTypography style={{ 
                      fontSize: '12px',
                      color: '#555',
                      whiteSpace: 'nowrap',
                      marginRight: '10px',
                    }}>
                      변경일자
                    </TranslateTypography>
                    <TextField 
                      defaultValue={ selectedDt } 
                      style={{ marginRight: '10px'}} 
                      onChange={(e) => setSelectedDt(e)}
                      type='date'
                    ></TextField>
                    <TranslateTypography style={{ 
                      fontSize: '12px',
                      color: '#555',
                      whiteSpace: 'nowrap',
                      margin: '0 10px',
                    }}>
                      작성자
                    </TranslateTypography>

                        <div style={{ width: '100%' }}>
                        <SelectAllCheckBox
                          setSelected={setSelectedUser}
                          defaultInputValue={selectedUser}
                          options={userData.map((user) => ({
                            value: user.USER,
                            label: user.USER
                          }))}
                          title=''
                          style={{
                            control: (base, state) => ({
                              ...base,
                              minHeight: 30,          // 전체 박스 높이
                              height: 30,
                              borderRadius: 4,
                              borderColor: state.isFocused ? '#7e7d98 !important' : base.borderColor, 
                              boxShadow: 'none',       
                              '&:hover': {
                                borderColor: '#ccc', 
                              },
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: '0 6px',
                              height: 30,             // 입력/선택 영역 높이
                            }),
                            indicatorsContainer: (base) => ({
                              ...base,
                              height: 30,             // 오른쪽 버튼 영역 높이
                            }),
                            input: (base) => ({
                              ...base,
                              margin: 0,
                              padding: 0,
                              fontSize: 13,
                            }),
                            singleValue: (base) => ({
                              ...base,
                              fontSize: 13,
                            }),
                            placeholder: (base) => ({
                              ...base,
                              fontSize: 13,
                            }),
                          }}
                        /></div>
                  </FlexBoxHorizontal>

                  <FlexBoxHorizontal style={{ width: '100%', justifyContent: 'flex-start', marginBottom: '10px' }}>
                    <TranslateTypography style={{ 
                      fontSize: '12px',
                      color: '#555',
                      whiteSpace: 'nowrap',
                      marginRight: '10px',
                    }}>
                      변경사항
                    </TranslateTypography>
                    <Textarea
                      style={{ height: '50px', width: '90%' }}
                      defaultValue={changeComment}
                      onChange={(e) => setChangeComment(e.target.value)}
                      value={changeComment}
                    />
                  </FlexBoxHorizontal>

                                    {/* 편집 모드 UI */}
                  <FlexBoxVertical style={{ gap: '15px', width: '100%' }}>
                    {/* 통합 이미지 선택 및 관리 영역 */}
                    <FlexBoxVertical 
                      ref={dropZoneRef}
                      style={{ 
                        cursor: 'pointer',
                        padding: '0 20px 20px 20px',
                        backgroundColor: '#fafafa',
                        transition: 'all 0.3s ease',
                        width: '80%',
                        marginTop: '10px'
                      }}
                      onDragOver={handleDragOverForComment}
                      onDrop={handleDropForComment}
                    >

                      <div style={{ fontSize: '12px', color: '#555', alignSelf: 'flex-end', marginTop: '10px' }}>
                            {'Selected '}
                            <span style={{ color: '#666', fontWeight: 'normal' }}>
                              : {selectedImages.length}
                            </span>
                          </div>
                      <FlexBoxVertical style={{ alignItems: 'center', marginBottom: '10px', height: 'auto' }}>         
                        <SimpleButton 
                          onClick={() => document.getElementById('comment-image-input').click()}
                          style={{ backgroundColor: palette.button.primary, width: 'auto', padding: '8px 16px' }}
                        >
                          {translateText({text: '이미지 추가 (다중)', globalLanguage: language})}
                        </SimpleButton>
                        {selectedImages.length > 0 && (
                          <SimpleButton 
                            onClick={clearAllImages}
                            style={{ width: 'auto',
                              backgroundColor: 'rgb(250, 250, 250)',
                              color: 'rgb(107, 114, 128)',
                              fontWeight: '500',
                              '&:hover': {
                              backgroundColor: 'rgb(250, 250, 250) !important'
                             } }}
                          >
                            ⟳ {translateText({text: '이미지 리셋', globalLanguage: language})}
                          </SimpleButton>
                        )}
                      </FlexBoxVertical>

                      {/* 통합 이미지 표시 영역 */}
                      {selectedImages.length > 0 && (
                        <FlexBoxVertical style={{ gap: '10px', maxHeight: '200px', overflowY: 'auto' }}>
                          <div style={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            overflowY: 'auto',
                            width: '60%',
                          }}>
                            {/* 모든 이미지들 표시 (기존 + 새로 추가) */}
                            {selectedImages.map((file, index) => {
                              // 이미지 URL 생성
                              let imageUrl;
                              try {
                                if (file.isExisting && file.dataUrl) {
                                  // 기존 이미지 (S3에서 다운로드한 것)
                                  imageUrl = file.dataUrl;
                                } else if (file instanceof File || file instanceof Blob) {
                                  // 새로 추가된 이미지
                                  imageUrl = URL.createObjectURL(file);
                                } else {
                                  imageUrl = 'https://static-dashff.fnf.co.kr/interstellar/no_image.png';
                                }
                              } catch (error) {
                                console.error('이미지 URL 생성 실패:', error);
                                imageUrl = 'https://static-dashff.fnf.co.kr/interstellar/no_image.png';
                              }

                              return (
                                <div key={`image-${index}`} style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '8px',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px',
                                  border: '1px solid #e9ecef'
                                }}>
                                  <img
                                    src={imageUrl}
                                    alt={`이미지 ${index + 1}`}
                                    style={{
                                      width: '50px',
                                      height: '50px',
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                      border: '1px solid #ddd',
                                      flexShrink: 0
                                    }}
                                    onError={(e) => {
                                      e.target.src = 'https://static-dashff.fnf.co.kr/interstellar/no_image.png';
                                    }}
                                  />
                                  <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minWidth: 0
                                  }}>
                                    <div style={{
                                      fontSize: '12px',
                                      color: '#666',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {file.name || file.fileName || `이미지 ${index + 1}`}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => removeSelectedImage(index)}
                                    style={{
                                      background: 'rgb(250, 250, 250)',
                                      color: 'red',
                                      border: 'none',
                                      borderRadius: '50%',
                                      width: '24px',
                                      height: '24px',
                                      fontSize: '20px',
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0
                                    }}
                                    title={`삭제`}
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </FlexBoxVertical>
                      )}
                      
                      {/* 안내 메시지 및 로딩 표시 */}
                      {isLoadingImages ? (
                        <div style={{ 
                          alignItems: 'center', 
                          gap: '10px', 
                          textAlign: 'center', 
                          fontSize: '14px', 
                          color: '#666',
                          padding: '20px',
                          backgroundColor: '#f8f9fa',
                        }}>
                          <div style={{ 
                            display: 'inline-block',
                            width: '20px',
                            height: '20px',
                            border: '2px solid #f3f3f3',
                            borderTop: '2px solid rgb(105, 105, 106)',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></div>
                        </div>
                      ) : selectedImages.length === 0 ? (
                        <div style={{ alignItems: 'center', gap: '5px', textAlign: 'center', fontSize: '12px', color: '#555' }}>
                          {translateText({text: '이미지를 추가하세요.', globalLanguage: language})}<br /> {translateText({text: '(Ctrl+V, 드래그 & 드롭, 버튼 클릭)', globalLanguage: language})}
                        </div>
                      ) : null}
                    </FlexBoxVertical>
  
                    <FlexBoxHorizontal style={{ gap: '10px', justifyContent: 'flex-end' }}>
                      <SimpleButton 
                        onClick={() => handleSave(mode)}
                        style={{
                          backgroundColor: (isSaving || uploadProgress !== null || isLoadingImages) ? '#94a3b8' : palette.button.primary,
                          width: 'auto',
                          padding: '8px 16px',
                          cursor: (isSaving || uploadProgress !== null || isLoadingImages) ? 'not-allowed' : 'pointer',
                          opacity: (isSaving || uploadProgress !== null || isLoadingImages) ? 0.6 : 1
                        }}
                        disabled={isSaving || uploadProgress !== null || isLoadingImages}
                      >
                        {isSaving ? 
                          (language === 'ko' ? '저장 중...' : 'Saving...') :
                          uploadProgress ? 
                            `${translateText({text: '저장 중...', globalLanguage: language})} (${uploadProgress.current}/${uploadProgress.total})` : 
                            translateText({text: '저장', globalLanguage: language})
                        }
                      </SimpleButton>
                      <SimpleButton 
                        onClick={handleCancelEdit}
                        style={{ 
                          backgroundColor: (isSaving || uploadProgress !== null || isLoadingImages) ? '#94a3b8' : 'rgb(107, 114, 128)',
                          width: 'auto',
                          padding: '8px 16px',
                          cursor: (isSaving || uploadProgress !== null || isLoadingImages) ? 'not-allowed' : 'pointer',
                          opacity: (isSaving || uploadProgress !== null || isLoadingImages) ? 0.6 : 1,
                          '&:hover': {
                            backgroundColor: '#4b5563 !important'
                          },
                        }}
                        disabled={isSaving || uploadProgress !== null || isLoadingImages}
                      >
                        {translateText({text: '취소', globalLanguage: language})}
                      </SimpleButton>
                    </FlexBoxHorizontal>
                  </FlexBoxVertical>
                  </>)
                    }
                    </>
                  )
                }                  
              </FlexBoxVertical>
          
          {commentData.length !== 0 && (
            // {/* 하단 데이터 그리드 */}
          <div style={{ width: '-webkit-fill-available', margin:'0 20px 10px 20px' }}>
          <DataGridPremium
                columns={translatedColumns}
                rows={commentData}
                    getRowId={(row) => row['seq']}
                    hideFooter
                rowHeight={30}
                sx={{
                  ...gridStyle,
                  '& .MuiDataGrid-row': {
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  },
                  '& .MuiDataGrid-row.Mui-selected': {
                    backgroundColor: '#e3f2fd !important',
                    '&:hover': {
                      backgroundColor: '#bbdefb !important',
                    },
                  },
                  '& .MuiDataGrid-scrollbar': {
                    overflowY: 'hidden',
                  },
                }}
                onRowClick={handleRowClick}
                rowSelectionModel={selectedComment ? [selectedComment.seq] : []}
                  />
          </div>
          )}
        </FlexBoxVertical>
      );
    };

    // 데이터 그리드 컬럼 정의
    const columns = (lauguage) => [
      { 
        field: 'change_dt', 
        headerName: '변경일자', 
        width: 120,
      },
      { 
        field: 'change_by', 
        headerName: '작성자', 
        width: 150
      },
      { 
        field: 'comment', 
        headerName: '변경사항', 
        width: 300,
      },
    ];

// 이미지 갤러리 컴포넌트
const ImageGallery = ({ selectedComment }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [selectedComment]);

  if (!selectedComment || !selectedComment.image_cnt || selectedComment.image_cnt === 0) {
    return (
      null
    );
  }

  // 이미지 URL 생성 함수
  const getImageUrl = (index) => {
    return `https://static-dashff.fnf.co.kr/interstellar/change_comment/${selectedComment.brd_cd}/${selectedComment.gtm_sesn}/${selectedComment.part_cd}_${selectedComment.seq}_${index + 1}.jpg?timestamp=${new Date().getTime()}`;
  };
  
  // 이미지 배열 생성
  const imageUrls = Array.from({ length: selectedComment.image_cnt }, (_, index) => getImageUrl(index));

  return (
    <div style={{ width: '100%' }}>
      {/* 메인 이미지 (큰 이미지) */}
      <div style={{ marginBottom: '10px' }}>
        <img
          src={imageUrls[selectedImageIndex]}
          alt={`메인 이미지 ${selectedImageIndex + 1}`}
          style={{
            width: '100%',
            height: '200px',
            objectFit: 'contain',
          }}
          onError={(e) => {
            e.target.src = 'https://static-dashff.fnf.co.kr/interstellar/no_image.png';
          }}
        />
      </div>

      {/* 썸네일 이미지들 (작은 이미지들) */}
      {selectedComment.image_cnt > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          position: 'relative'
        }}>
           {/* 왼쪽 화살표 */}
           <button
            onClick={() => {
              if (selectedImageIndex > 0) {
                const newIndex = selectedImageIndex - 1;
                setSelectedImageIndex(newIndex);
                
                // 선택된 썸네일을 중앙으로 스크롤
                const container = document.getElementById('thumbnail-container');
                if (container) {
                  const thumbnailWidth = 60;
                  const gap = 8;
                  const containerWidth = container.clientWidth;
                  const thumbnailWithGap = thumbnailWidth + gap;
                  const scrollPosition = (thumbnailWithGap * newIndex) - (containerWidth / 2) + (thumbnailWidth / 2);
                  
                  container.scrollTo({
                    left: Math.max(0, scrollPosition),
                    behavior: 'smooth'
                  });
                }
              }
            }}
            disabled={selectedImageIndex === 0}
            style={{
              background: 'white',
              color: selectedImageIndex === 0 ? '#ccc' : 'rgb(107, 114, 128)',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              flexShrink: 0
            }}
          >
            ❮
          </button>


          {/* 썸네일 컨테이너 */}
          <div 
            id="thumbnail-container"
            style={{
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none', /* Firefox */
              msOverflowStyle: 'none', /* IE/Edge */
              flex: 1,
              padding: '4px 0'
            }}
            css={`
              &::-webkit-scrollbar {
                display: none; /* Chrome/Safari */
              }
            `}
          >
{imageUrls.map((url, index) => (
  <img
    key={index}
    src={url}
    alt={`썸네일 ${index + 1}`}
    style={{
      width: '60px',
      height: '60px',
      objectFit: 'cover',
      borderRadius: '4px',
      border: selectedImageIndex === index ? '2px solid #4caf50' : '2px solid white',
      cursor: 'pointer',
      transition: 'border-color 0.2s ease',
      flexShrink: 0 // 썸네일 크기 고정
    }}
    onClick={() => {
      setSelectedImageIndex(index);
      // 클릭한 썸네일을 중앙으로 스크롤
      const container = document.getElementById('thumbnail-container');
      if (container) {
        const thumbnailWidth = 60; // 썸네일 너비
        const gap = 8; // gap 값
        const containerWidth = container.clientWidth;
        const thumbnailWithGap = thumbnailWidth + gap;
        const scrollPosition = (thumbnailWithGap * index) - (containerWidth / 2) + (thumbnailWidth / 2);
        
        container.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      }
    }}
    onError={(e) => {
      e.target.src = 'https://static-dashff.fnf.co.kr/interstellar/no_image.png';
    }}
  />
))}
          </div>

          {/* 오른쪽 화살표 */}
          <button
            onClick={() => {
              if (selectedImageIndex < selectedComment.image_cnt - 1) {
                const newIndex = selectedImageIndex + 1;
                setSelectedImageIndex(newIndex);
                
                // 선택된 썸네일을 중앙으로 스크롤
                const container = document.getElementById('thumbnail-container');
                if (container) {
                  const thumbnailWidth = 60;
                  const gap = 8;
                  const containerWidth = container.clientWidth;
                  const thumbnailWithGap = thumbnailWidth + gap;
                  const scrollPosition = (thumbnailWithGap * newIndex) - (containerWidth / 2) + (thumbnailWidth / 2);
                  
                  container.scrollTo({
                    left: Math.max(0, scrollPosition),
                    behavior: 'smooth'
                  });
                }
              }
            }}
            disabled={selectedImageIndex === selectedComment.image_cnt - 1}
            style={{
              background: 'white',
              color: selectedImageIndex === selectedComment.image_cnt - 1 ? '#ccc' : 'rgb(107, 114, 128)',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              flexShrink: 0
            }}
          >
            ❯
          </button>
        </div>
      )}
    </div>
  );
};

export default ChangeCommentTab;

    
    // 깔끔한 통일 버튼 스타일
    const SimpleButton = styled.button`
      padding: ${props => props.small ? '4px 8px' : '8px 16px'};
      background-color: ${props => props.style?.backgroundColor || '#4f46e5'};
      color: white;
      border: none;
      border-radius: 4px;
      font-size: ${props => props.small ? '10px' : '12px'};
      font-weight: 500;
      cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
      transition: background-color 0.2s ease;
      width: ${props => props.small ? '70px' : '140px'};
      
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      opacity: ${props => props.disabled ? 0.8 : 1};
      
      &:hover {
        background-color: ${props => props.disabled ? props.style?.backgroundColor : (props.style?.['&:hover']?.backgroundColor || '#4338ca !important')};
      }
      
      &:active {
        background-color: ${props => props.disabled ? props.style?.backgroundColor : (props.style?.['&:active']?.backgroundColor || '#3730a3')};
      }
    `;
    
const gridStyle = {
    width: '100%',
    fontSize: '0.8rem',
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: '#F9F9FB !important',
      height: '40px !important',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold',
    },
    '& .Mui-selected': {
      backgroundColor: 'rgba(0, 0, 0, 0.03) !important',
    },
    '& .MuiCheckbox-root': {
      color: '#666666',
      '&.Mui-checked': {
        color: '#666666',
      }
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
      height: '40px !important',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none'
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none'
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none'
    },
    '& .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none'
    }
  };